import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridConfig } from './GridConfig';
import { TasksGridDataProvider } from '../dataProvider/TasksGridDataProvider';
import {
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_TITLE,
  WEB_COMPONENT_NETWORK_SUB_TASKS,
} from '../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../grid/gridView';
import { Column, ColumnKeys, Dependencies, Task, TaskSelectionStatus } from '../types';
import { GridViewMetadata, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { SubTasksWebComponent } from '../../networkSubTasks';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';

export class TasksView extends BaseWebComponentView {
  public dom: {
    $grid: GridViewWebComponent<Column, ColumnKeys>;
    $deleteBtn: GzButtonWebComponent;
    $retryBtn: GzButtonWebComponent;
    $actionsBtn: HTMLElement;
    $title: HTMLElement;
    $hostElement: HTMLElement;
    $subTasks: SubTasksWebComponent;
  };
  public dependencies: Dependencies;

  public getDomElements(): void {
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$title = this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE);
    this.dom.$deleteBtn = this.shadow.querySelector('#actionDelete');
    this.dom.$retryBtn = this.shadow.querySelector('#retryBtn');
    this.dom.$hostElement = this.shadow;
    this.dom.$subTasks = this.shadow.querySelector(WEB_COMPONENT_NETWORK_SUB_TASKS);

    this.getTranslations();
  }

  public buildView(): void {
    const { viewMetaData } = new GridConfig().getDataForView(this.dependencies);
    this.buildGrid(viewMetaData);
    this.injectDependenciesInChildren();
    this.setTranslations();
    this.buildSubTasksPage();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getTasksStrings();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  private buildGrid(viewMetaData: GridViewMetadata<Column, ColumnKeys>): void {
    const grid = this.dom.$grid;
    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      compactView: viewMetaData.isCompactViewChecked,
      displayedColumns: viewMetaData.columns,
      filters: viewMetaData.filters,
      name: '',
      pageSize: viewMetaData.pageSize,
      type: ViewType.DEFAULT,
      id: viewMetaData.viewId,
    };
    grid.propertiesSet = false;
    grid.dataProvider = new TasksGridDataProvider(this.dependencies);
    grid.sideMenuParent = this.shadow;

    for (const [key, value] of Object.entries(viewMetaData)) {
      grid[key] = value;
    }
    grid.defaultViewProps = defaultViewProps;
    grid.propertiesSet = true;
    grid.injectDependencies(this.dependencies);
    grid.start().catch();
  }

  private setTranslations(): void {
    this.dom.$title.textContent = this.dependencies.translateFn('Tasks');
    this.dom.$deleteBtn.textContent = this.dependencies.translateFn('Delete');
    this.dom.$retryBtn.textContent = this.dependencies.translateFn('Restart');
  }

  public getRetryConfirmationDialogFooterHtml(): string {
    return `<gz-button id='confirm' color='primary' class='mr-2'>
        ${this.dependencies.translateFn('RESTART')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('CANCEL')}
      </gz-button>
    `;
  }

  public getDeleteConfirmationDialogFooterHtml(): string {
    return `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
        ${this.dependencies.translateFn('DELETE')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('CANCEL')}
      </gz-button>`;
  }

  private buildSubTasksPage() {
    this.dom.$subTasks.isVisible = false;
  }

  // create special task
  public showSubTasksPage(task: Task): void {
    if (isEmpty(task)) {
      return;
    }
    this.dom.$subTasks.propertiesSet = false;
    this.dom.$subTasks.isVisible = true;
    this.dom.$subTasks.task = task;
    this.dom.$subTasks.propertiesSet = true;
  }

  public hideSubTasksPage(): void {
    this.dom.$subTasks.propertiesSet = false;
    this.dom.$subTasks.isVisible = false;
    this.dom.$subTasks.task = undefined;
    this.dom.$subTasks.propertiesSet = true;
  }

  private injectDependenciesInChildren() {
    if (!this.dom.$subTasks.injectDependencies) {
      return;
    }

    this.dom.$subTasks.injectDependencies(this.dependencies);
    this.dom.$subTasks.start().catch(console.error);
  }

  public calculateBackBtnPosition() {
    const offSet = 44;
    const backButtonPosition = window.innerHeight - this.shadow.host.clientHeight - offSet;
    this.shadow.host.style.setProperty('--back-btn-position', backButtonPosition + 'px');
  }

  public getDeleteConfirmationDialogWindowStrings(selectionStatus: TaskSelectionStatus): {title: string, message: string} {
    return this.translations.deleteDialog[selectionStatus];
  }

}
